import { useState } from "react";

import {
  HEALTHLINK_STATUS_FILTER,
  matchesHealthlinkStatus,
  matchesSystemStatus,
  SYSTEM_STATUS_FILTER,
} from "features/vaccineSettings/filters";
import { getVaccinesRowActions } from "features/vaccineSettings/rowActions";
import { EMPTY_CONFIRM_DIALOG } from "features/vaccineSettings/utils";

import { useDeleteVaccine } from "./useDeleteVaccine";
import { useSaveVaccine } from "./useSaveVaccine";
import { useSettingsVaccineCodes } from "./useSettingsVaccineCodes";
import { useSettingsVaccines } from "./useSettingsVaccines";
import { useToggleVaccine } from "./useToggleVaccine";
import { useVaccineListFilters } from "./useVaccineListFilters";

const SYSTEM_SEARCH_KEYS = ["name", "manufacturer"];
const HEALTHLINK_SEARCH_KEYS = ["name", "saleProprietaryName", "code"];

export const useVaccinesPage = () => {
  const [activeTab, setActiveTab] = useState("system");
  const [showModal, setShowModal] = useState(false);
  const [selectedVaccine, setSelectedVaccine] = useState(null);
  const [selectedVaccineId, setSelectedVaccineId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(EMPTY_CONFIRM_DIALOG);

  const { data: vaccines = [], isLoading: isLoadingVaccines } = useSettingsVaccines();
  const { data: vaccineCodes = [], isLoading: isLoadingCodes } = useSettingsVaccineCodes();
  const { mutate: deleteVaccine, isPending: isDeleting } = useDeleteVaccine();
  const { mutate: toggleVaccine, isPending: isToggling } = useToggleVaccine();
  const { mutate: saveVaccine } = useSaveVaccine();

  const systemFilters = useVaccineListFilters({
    rows: vaccines,
    statusFilter: SYSTEM_STATUS_FILTER,
    matchesStatus: matchesSystemStatus,
    typeKey: "type",
    searchKeys: SYSTEM_SEARCH_KEYS,
  });

  const healthlinkFilters = useVaccineListFilters({
    rows: vaccineCodes,
    statusFilter: HEALTHLINK_STATUS_FILTER,
    matchesStatus: matchesHealthlinkStatus,
    typeKey: "vaccineType",
    searchKeys: HEALTHLINK_SEARCH_KEYS,
  });

  const closeConfirmDialog = () => setConfirmDialog(EMPTY_CONFIRM_DIALOG);

  const handleAddVaccine = () => {
    setSelectedVaccine(null);
    setSelectedVaccineId(null);
    setShowModal(true);
  };

  const handleEditVaccine = (vaccine) => {
    setSelectedVaccine(vaccine);
    setSelectedVaccineId(vaccine.id);
    setShowModal(true);
  };

  const handleDeleteVaccine = (vaccine) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Vaccine",
      message: `Are you sure you want to delete "${vaccine.name}"?`,
      confirmLabel: "Delete",
      confirmVariant: "primary",
      onConfirm: () => deleteVaccine(vaccine, { onSuccess: closeConfirmDialog }),
    });
  };

  const handleToggleVaccine = (vaccine) => {
    const enabling = vaccine.disabled === true;

    setConfirmDialog({
      isOpen: true,
      title: enabling ? "Enable Vaccine" : "Disable Vaccine",
      message: `Are you sure you want to ${enabling ? "enable" : "disable"} "${vaccine.name}"?`,
      confirmLabel: enabling ? "Enable" : "Disable",
      confirmVariant: "primary",
      onConfirm: () =>
        toggleVaccine({ ...vaccine, disabled: !enabling }, { onSuccess: closeConfirmDialog }),
    });
  };

  const handleSaveVaccine = (vaccineData) => {
    const data = selectedVaccineId
      ? { ...vaccineData, id: selectedVaccineId, disabled: selectedVaccine?.disabled }
      : vaccineData;
    saveVaccine(data, { onSuccess: () => setShowModal(false) });
  };

  const rowActions = getVaccinesRowActions({
    onToggle: handleToggleVaccine,
    onEdit: handleEditVaccine,
    onDelete: handleDeleteVaccine,
  });

  return {
    activeTab,
    setActiveTab,
    showModal,
    setShowModal,
    selectedVaccine,
    confirmDialog,
    closeConfirmDialog,
    vaccines: systemFilters.filteredRows,
    isLoadingVaccines,
    systemFilters,
    vaccineCodes: healthlinkFilters.filteredRows,
    isLoadingCodes,
    healthlinkFilters,
    isDeleting,
    isToggling,
    rowActions,
    handleAddVaccine,
    handleSaveVaccine,
  };
};
